import { useEffect, useRef, useState } from 'react';
import { type BaseMultiPageModel, UndoRedoManager } from '@bynder-studio/render-core';
import { isCtrlKey } from 'packages/helpers/hotKeys';
import { useIsModalOpen } from './useIsModalOpen';

export default function useEditorUndoRedo(creativeModel: BaseMultiPageModel) {
    const [canUndo, setCanUndo] = useState(false);
    const isModalOpen = useIsModalOpen();
    const undoRedoManagerRef = useRef<UndoRedoManager | null>(null);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        const undoRedoManagerInstance = new UndoRedoManager(creativeModel);
        undoRedoManagerRef.current = undoRedoManagerInstance;

        const handlerUpdate = (event) => {
            setCanUndo(event.canUndo);
        };

        creativeModel.eventEmitter.on('undoRedoStateUpdate', handlerUpdate);

        return () => {
            creativeModel.eventEmitter.off('undoRedoStateUpdate', handlerUpdate);
            undoRedoManagerRef.current = null;
        };
    }, [creativeModel]);

    useEffect(() => {
        if (!creativeModel || isModalOpen) {
            return;
        }

        const handleUndoRedo = (event) => {
            if (!isCtrlKey(event)) {
                return;
            }

            if (event.code === 'KeyZ' || event.keyCode === 90) {
                event.preventDefault();

                if (event.shiftKey) {
                    undoRedoManagerRef.current?.redo();
                } else {
                    undoRedoManagerRef.current?.undo();
                }
            }
        };

        window.addEventListener('keydown', handleUndoRedo);

        return () => {
            window.removeEventListener('keydown', handleUndoRedo);
        };
    }, [creativeModel, isModalOpen]);

    return {
        canUndo,
    };
}
